import axios from "axios";
import { baseURL } from "../Constants";

// Admin-only control + status for the rating-only Rating 3.0 v3 recompute (tasks/rating-recompute.md).
const url = `${baseURL}/admin/recompute`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

export interface RecomputeChange {
    gameId: string;
    matchId: string | null;
    mapName: string | null;
    steamId: string;
    name: string | null;
    old: number | null;
    new: number | null;
}

export interface RecomputeProblem {
    gameId: string;
    matchId: string | null;
    mapName: string | null;
    status: "failed" | "skipped";
    reason: string | null;
    attempts: number;
}

export interface RecomputeStatus {
    season: number;
    state: {
        season: number;
        running: boolean;
        concurrency: number;
        respectWindow: boolean;
        appliedAt: string | null;
        revertedAt: string | null;
    } | null;
    note: string | null;
    inParseWindow: boolean;
    current: { gameId: string; mapName: string | null; seconds: number }[];
    report: {
        counts: { pending: number; running: number; done: number; applied: number; skipped: number; failed: number };
        avgSecondsPerMap: number | null;
        parity: { maps: number; maxAbsDelta: number | null; off: number };
        changes: { playerMaps: number; meanAbsDelta: number | null; over005: number; over010: number };
        topChanges: RecomputeChange[];
        problems: RecomputeProblem[];
    };
}

export const getRecomputeStatus = async (season?: number): Promise<RecomputeStatus> =>
    (await axios.get<RecomputeStatus>(`${url}/status`, { ...auth(), params: season ? { season } : {} })).data;

export const startRecompute = async (season: number, concurrency: number, respectWindow: boolean) =>
    (await axios.post<{ added: number }>(`${url}/start`, { season, concurrency, respectWindow }, auth())).data;

export const pauseRecompute = async () => { await axios.post(`${url}/pause`, {}, auth()); };

export const saveRecomputeSettings = async (concurrency: number, respectWindow: boolean) => {
    await axios.post(`${url}/settings`, { concurrency, respectWindow }, auth());
};

export const retryFailedRecompute = async (season: number) =>
    (await axios.post<{ requeued: number }>(`${url}/retry-failed`, { season }, auth())).data;

export const applyRecompute = async (season: number) =>
    (await axios.post<{ maps: number; rows: number }>(`${url}/apply`, { season }, auth())).data;

export const revertRecompute = async (season: number) =>
    (await axios.post<{ maps: number; rows: number }>(`${url}/revert`, { season }, auth())).data;

export interface RecomputeTeamChange {
    gameId: string;
    matchId: string | null;
    mapName: string | null;
    matchTime: string | null;
    status: "done" | "applied";
    steamId: string;
    name: string | null;
    old: number | null;
    new: number | null;
    oldSwing: number | null;
    newSwing: number | null;
    rounds: number | null;
    teamScore: number | null;
    oppScore: number | null;
}

// Old → new per map for one team's players (team page id or alias id).
export const getRecomputeTeam = async (season: number, teamId: string): Promise<RecomputeTeamChange[]> =>
    (await axios.get<RecomputeTeamChange[]>(`${url}/team`, { ...auth(), params: { season, teamId } })).data;
